// POS Service - Separate from existing sales service
import { apiService } from './apiService';
import { RBACService } from '../utils/rbac';
import {
  POSCartItem,
  POSPaymentMethod,
  POSProduct,
  POSStashedTransaction,
  POSTransaction,
  POSCoupon,
  POSPaymentDetailRecord,
} from '../types/pos';
import { mergeSplitPaymentCache, saveSplitPaymentCache } from '../utils/posSplitPaymentCache';
import { getReceiptPaymentLines } from '../components/pos/ReceiptPaymentMethods';

function mapPaymentDetailsFromApi(details: unknown): POSPaymentDetailRecord[] | undefined {
  if (!Array.isArray(details) || details.length === 0) return undefined;
  return details.map((d: any) => ({
    id: d.id,
    payment_method: String(d.payment_method),
    amount: parseFloat(String(d.amount)),
    reference_number: d.reference_number || undefined,
    payer_phone: d.payer_phone || undefined,
  }));
}

function mapPOSTransactionFromApi(txn: any): POSTransaction {
  const paymentDetails = mapPaymentDetailsFromApi(txn.payment_details);
  const paymentMethod =
    paymentDetails && paymentDetails.length > 1
      ? POSPaymentMethod.SPLIT
      : (txn.payment_method as POSPaymentMethod);
  const mapped: POSTransaction = {
    id: txn.id,
    transaction_number: txn.transaction_number,
    serial: txn.serial || '',
    location_id: txn.location_id,
    location_name: txn.location_name,
    sale_date: txn.sale_date,
    sale_time: new Date(txn.created_at).toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' }),
    payment_method: paymentMethod,
    payment_reference: txn.payment_reference || undefined,
    payer_phone: txn.payer_phone || undefined,
    receipt_printed: txn.receipt_printed !== false,
    payment_details: paymentDetails,
    total_amount: parseFloat(txn.total_amount.toString()),
    tax_amount: parseFloat(txn.tax_amount.toString()),
    discount_amount: parseFloat(txn.discount_amount.toString()),
    coupon_discount: parseFloat(txn.coupon_discount?.toString() || '0'),
    served_by: txn.served_by,
    cashier_name: txn.served_by_name,
    created_at: txn.created_at,
    status: txn.status,
    is_refund: txn.is_refund,
    is_tax_invoice: Boolean(txn.is_tax_invoice),
    tax_invoice_number: txn.tax_invoice_number || null,
    taxable_amount: txn.taxable_amount != null ? parseFloat(String(txn.taxable_amount)) : null,
    exempt_amount: txn.exempt_amount != null ? parseFloat(String(txn.exempt_amount)) : null,
    zero_rated_amount: txn.zero_rated_amount != null ? parseFloat(String(txn.zero_rated_amount)) : null,
    buyer_name: txn.buyer_name || null,
    buyer_cid: txn.buyer_cid || null,
    buyer_tpn: txn.buyer_tpn || null,
    buyer_address: txn.buyer_address || null,
    supplier_name: txn.supplier_name || null,
    supplier_tpn: txn.supplier_tpn || null,
    supplier_address: txn.supplier_address || null,
    gst_prices_inclusive: txn.gst_prices_inclusive ?? null,
    items: (txn.transaction_items || []).map((item: any) => {
      const up = parseFloat(item.unit_price.toString());
      const tp = parseFloat(item.total_price.toString());
      return {
        id: item.id?.toString() || '',
        product_id: item.product_id,
        product_code: item.product_code,
        product_name: item.product_name,
        barcode: item.barcode,
        quantity: item.quantity,
        unit_price: up,
        sale_unit_price: up,
        total_price: tp,
        gst_category: item.gst_category || undefined,
        gst_rate: item.gst_rate != null ? parseFloat(String(item.gst_rate)) : undefined,
        gst_amount: item.gst_amount != null ? parseFloat(String(item.gst_amount)) : undefined,
      };
    }),
  };
  return mergeSplitPaymentCache(mapped);
}

// Mock data for development (fallback only - will use API calls)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _stashedTransactions: POSStashedTransaction[] = [
  {
    id: 'stash-1',
    serial: '20260108001',
    time: '10:30 AM',
    items: [
      {
        id: 'cart-item-1',
        product_id: 1,
        product_code: 'WAT001',
        product_name: 'Alhazaa Ice Bag 4kg',
        barcode: '1234567890',
        quantity: 3,
        unit_price: 0.500,
        sale_unit_price: 0.500,
        total_price: 1.500,
      },
      {
        id: 'cart-item-2',
        product_id: 5,
        product_code: 'CHO001',
        product_name: 'Maltesers Chocolate 37gm',
        barcode: '6271000733821',
        quantity: 2,
        unit_price: 0.400,
        sale_unit_price: 0.400,
        total_price: 0.800,
      },
      {
        id: 'cart-item-3',
        product_id: 9,
        product_code: 'DRK001',
        product_name: 'Red Bull Energy Drink 250ml',
        barcode: '1234567898',
        quantity: 1,
        unit_price: 0.650,
        sale_unit_price: 0.650,
        total_price: 0.650,
      },
    ],
    total_amount: 2.950,
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
  },
  {
    id: 'stash-2',
    serial: '20260108002',
    time: '11:15 AM',
    items: [
      {
        id: 'cart-item-4',
        product_id: 2,
        product_code: 'WAT002',
        product_name: 'Abc Wellness Water 330ml',
        barcode: '1234567891',
        quantity: 5,
        unit_price: 0.150,
        sale_unit_price: 0.150,
        total_price: 0.750,
      },
      {
        id: 'cart-item-5',
        product_id: 6,
        product_code: 'CHO002',
        product_name: 'Loacker Tortina Bicult Milk Choc 21gm',
        barcode: '1234567895',
        quantity: 4,
        unit_price: 0.350,
        sale_unit_price: 0.350,
        total_price: 1.400,
      },
    ],
    total_amount: 2.150,
    created_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago
  },
  {
    id: 'stash-3',
    serial: '20260108003',
    time: '2:45 PM',
    items: [
      {
        id: 'cart-item-6',
        product_id: 10,
        product_code: 'DT001',
        product_name: 'Baraka Dates Medjool Peanut Butter 2pcs',
        barcode: '6271000733821',
        quantity: 2,
        unit_price: 1.500,
        sale_unit_price: 1.500,
        total_price: 3.000,
      },
      {
        id: 'cart-item-7',
        product_id: 8,
        product_code: 'BEV001',
        product_name: 'Barbican Strawberry Malt Beverage 330ml',
        barcode: '1234567897',
        quantity: 3,
        unit_price: 0.350,
        sale_unit_price: 0.350,
        total_price: 1.050,
      },
      {
        id: 'cart-item-8',
        product_id: 7,
        product_code: 'SNK001',
        product_name: 'Best Salted Peanuts 30gm',
        barcode: '1234567896',
        quantity: 2,
        unit_price: 0.350,
        sale_unit_price: 0.350,
        total_price: 0.700,
      },
    ],
    total_amount: 4.750,
    created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
  },
];
let transactionHistory: POSTransaction[] = [];

/** Normalize cart lines from API / JSONB (older stashes may omit sale_unit_price). */
function mapCartItemFromPayload(raw: any, idFallback: string): POSCartItem {
  const qty = Number(raw.quantity) || 1;
  const list = Number(raw.unit_price) || 0;
  const saleRaw = raw.sale_unit_price;
  const sale =
    saleRaw != null && saleRaw !== '' && Number.isFinite(Number(saleRaw)) ? Number(saleRaw) : list;
  return {
    id: raw.id != null && raw.id !== '' ? String(raw.id) : idFallback,
    product_id: Number(raw.product_id),
    product_code: raw.product_code,
    product_name: raw.product_name,
    barcode: raw.barcode,
    quantity: qty,
    unit_price: list,
    sale_unit_price: sale,
    total_price: qty * sale,
    stock_quantity: raw.stock_quantity != null ? Number(raw.stock_quantity) : undefined,
    gst_category: raw.gst_category || undefined,
    gst_rate: raw.gst_rate != null ? Number(raw.gst_rate) : undefined,
    gst_amount: raw.gst_amount != null ? Number(raw.gst_amount) : undefined,
  };
}

function getCurrentLocationId(fallback: number = 0): number {
  const u: any = RBACService.getCurrentUser();
  const raw = u?.locationId ?? u?.location_id;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export type POSTaxSettings = {
  gst_registered: boolean;
  gst_prices_inclusive: boolean;
  standard_rate: number;
  buyer_threshold: number;
  tax_id?: string | null;
  legal_name?: string | null;
  address?: string | null;
  phone?: string | null;
  payment_account_name?: string | null;
  payment_account_number?: string | null;
  payment_account_bank?: string | null;
};

export async function getPOSTaxSettings(locationId?: number): Promise<POSTaxSettings> {
  const params: Record<string, number> = {};
  const loc = locationId ?? getCurrentLocationId();
  if (loc) params.location_id = loc;
  return apiService.get<POSTaxSettings>('/pos/tax-settings', { params });
}

// Get products for POS (quick selection)
export async function getPOSProducts(search?: string, category?: string, locationId?: number): Promise<POSProduct[]> {
  try {
    const params: any = { _ts: Date.now() };
    if (search) params.search = search;
    if (category && category !== 'All') params.category = category;
    const loc = locationId ?? getCurrentLocationId();
    if (loc) params.location_id = loc;
    
    const response = await apiService.get<POSProduct[]>('/pos/products', { params });
    return (response || []).map((p) => ({
      ...p,
      stock_quantity: Math.max(0, Number(p.stock_quantity) || 0),
    }));
  } catch (error) {
    console.error('Error fetching POS products:', error);
    // Fallback to empty array on error
    return [];
  }
}

/** Full product catalog for offline POS cache (includes zero-stock items). */
export async function getPOSProductCatalog(locationId: number): Promise<POSProduct[]> {
  try {
    const response = await apiService.get<POSProduct[]>('/pos/products/catalog', {
      params: { location_id: locationId },
    });
    return response;
  } catch (error) {
    console.error('Error fetching POS product catalog:', error);
    throw error;
  }
}

// Get product by barcode
export async function getProductByBarcode(barcode: string, locationId?: number): Promise<POSProduct | null> {
  try {
    const params: any = {};
    const loc = locationId ?? getCurrentLocationId();
    if (loc) params.location_id = loc;
    const response = await apiService.get<POSProduct>(
      `/pos/products/barcode/${encodeURIComponent(barcode)}`,
      { params },
    );
    return response;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    console.error('Error fetching product by barcode:', error);
    return null;
  }
}

// Get quick categories
export async function getQuickCategories(): Promise<string[]> {
  try {
    const products = await getPOSProducts();
    const categories = new Set(products.map(p => p.category || 'Other'));
    return ['All', ...Array.from(categories)];
  } catch (error) {
    console.error('Error fetching categories:', error);
    return ['All'];
  }
}

// Stash (suspend) transaction
export async function stashTransaction(items: POSCartItem[]): Promise<POSStashedTransaction> {
  try {
    const totalAmount = items.reduce((sum, item) => sum + item.total_price, 0);
    
    // Use logged-in user's location (avoid hardcoded locationId=1)
    const userLocationId = getCurrentLocationId(0);
    if (!userLocationId) {
      throw new Error('No location assigned to your account. Please contact your administrator.');
    }
    
    const response = await apiService.post<any>('/pos/stash', {
      location_id: userLocationId,
      total_amount: totalAmount,
      items: items.map(item => ({
        product_id: item.product_id,
        product_code: item.product_code,
        product_name: item.product_name,
        barcode: item.barcode,
        quantity: item.quantity,
        unit_price: item.unit_price,
        sale_unit_price: item.sale_unit_price,
        total_price: item.total_price
      })),
      notes: null
    });
    
    // Format response to match frontend interface
    return {
      id: response.id.toString(),
      serial: response.serial,
      time: new Date(response.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
      items: (response.items || []).map((it: any, idx: number) =>
        mapCartItemFromPayload(it, `stash-line-${idx}`)
      ),
      total_amount: parseFloat(response.total_amount.toString()),
      created_at: response.created_at
    };
  } catch (error) {
    console.error('Error stashing transaction:', error);
    throw error;
  }
}

// Get stashed transactions
export async function getStashedTransactions(): Promise<POSStashedTransaction[]> {
  try {
    const response = await apiService.get<any[]>('/pos/stashed');
    
    // Format response to match frontend interface
    return response.map(stash => ({
      id: stash.id.toString(),
      serial: stash.serial,
      time: new Date(stash.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
      items: (stash.items || []).map((it: any, idx: number) =>
        mapCartItemFromPayload(it, `stash-line-${idx}`)
      ),
      total_amount: parseFloat(stash.total_amount.toString()),
      created_at: stash.created_at
    }));
  } catch (error) {
    console.error('Error fetching stashed transactions:', error);
    return [];
  }
}

// Delete stashed transaction
export async function deleteStashedTransaction(id: string): Promise<void> {
  try {
    await apiService.delete(`/pos/stashed/${id}`);
  } catch (error) {
    console.error('Error deleting stashed transaction:', error);
    throw error;
  }
}

// Complete POS transaction
export async function completePOSTransaction(
  items: POSCartItem[],
  payment: {
    method: POSPaymentMethod;
    amount: number;
    reference_number?: string;
    payer_phone?: string;
  } | null,
  locationId: number,
  opts?: {
    tax_amount?: number;
    couponId?: number;
    client_sale_id?: string;
    buyer_name?: string;
    buyer_cid?: string;
    buyer_tpn?: string;
    buyer_address?: string;
    payer_phone?: string;
    receipt_printed?: boolean;
    credit_customer_name?: string;
    payment_details?: Array<{
      payment_method: POSPaymentMethod;
      amount: number;
      reference_number?: string;
      payer_phone?: string;
    }>;
  },
): Promise<POSTransaction> {
  try {
    const taxAmount = Math.max(0, Number(opts?.tax_amount) || 0);
    const splitDetails = opts?.payment_details;
    const body: Record<string, unknown> = {
      location_id: locationId,
      items: items.map(item => {
        const charged =
          item.sale_unit_price != null && Number.isFinite(item.sale_unit_price) && item.sale_unit_price >= 0
            ? item.sale_unit_price
            : item.unit_price;
        const unitsPer = Math.max(1, Math.floor(Number(item.units_per) || 1));
        const sellQty = Math.max(1, Math.floor(Number(item.quantity) || 1));
        const baseQty = sellQty * unitsPer;
        const lineTotal = sellQty * charged;
        const unitPerBase = baseQty > 0 ? Math.round((lineTotal / baseQty) * 1000) / 1000 : charged;
        return {
          product_id: item.product_id,
          product_code: item.product_code,
          product_name: item.product_name,
          barcode: item.barcode,
          quantity: baseQty,
          unit_price: unitPerBase,
          total_price: lineTotal,
        };
      }),
      coupon_code: null,
      notes: null,
      tax_amount: taxAmount,
      receipt_printed: opts?.receipt_printed !== false,
    };

    if (opts?.client_sale_id) {
      body.client_sale_id = opts.client_sale_id;
    }
    if (opts?.buyer_name) body.buyer_name = opts.buyer_name;
    if (opts?.buyer_cid) body.buyer_cid = opts.buyer_cid;
    if (opts?.buyer_tpn) body.buyer_tpn = opts.buyer_tpn;
    if (opts?.buyer_address) body.buyer_address = opts.buyer_address;
    if (opts?.credit_customer_name) body.credit_customer_name = opts.credit_customer_name;

    if (splitDetails && splitDetails.length > 0) {
      body.payment_method = 'SPLIT';
      body.payment_details = splitDetails.map((line) => ({
        payment_method: line.payment_method,
        amount: line.amount,
        reference_number: line.reference_number || null,
        payer_phone: line.payer_phone || opts?.payer_phone || null,
      }));
      body.payer_phone = opts?.payer_phone || splitDetails.find((l) => l.payer_phone)?.payer_phone || null;
    } else if (payment) {
      body.payment_method = payment.method;
      body.payment_reference = payment.reference_number || null;
      body.payer_phone = payment.payer_phone || opts?.payer_phone || null;
    }

    const response = await apiService.post<any>('/pos/complete', body);

    const responseDetails = mapPaymentDetailsFromApi(response.payment_details);
    const fallbackDetails =
      !responseDetails?.length && splitDetails?.length
        ? splitDetails.map((line) => ({
            payment_method: String(line.payment_method),
            amount: line.amount,
            reference_number: line.reference_number || undefined,
            payer_phone: line.payer_phone || undefined,
          }))
        : undefined;
    const paymentDetails = responseDetails?.length ? responseDetails : fallbackDetails;
    const paymentMethod =
      paymentDetails && paymentDetails.length > 1
        ? POSPaymentMethod.SPLIT
        : (response.payment_method as POSPaymentMethod);
    
    const result: POSTransaction = {
      id: response.id,
      transaction_number: response.transaction_number,
      serial: response.serial || '',
      location_id: response.location_id,
      location_name: response.location_name,
      sale_date: response.sale_date,
      sale_time: new Date(response.created_at).toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' }),
      payment_method: paymentMethod,
      payment_reference: response.payment_reference || undefined,
      payer_phone: response.payer_phone || undefined,
      receipt_printed: response.receipt_printed !== false,
      payment_details: paymentDetails,
      total_amount: parseFloat(response.total_amount.toString()),
      tax_amount: parseFloat(response.tax_amount.toString()),
      discount_amount: parseFloat(response.discount_amount.toString()),
      coupon_discount: parseFloat(response.coupon_discount?.toString() || '0'),
      served_by: response.served_by,
      cashier_name: response.served_by_name,
      created_at: response.created_at,
      items: (response.transaction_items || []).map((item: any) => {
        const up = parseFloat(item.unit_price.toString());
        const tp = parseFloat(item.total_price.toString());
        return {
          id: item.id?.toString() || '',
          product_id: item.product_id,
          product_code: item.product_code,
          product_name: item.product_name,
          barcode: item.barcode,
          quantity: item.quantity,
          unit_price: up,
          sale_unit_price: up,
          total_price: tp,
        };
      }),
    };
    if (paymentDetails && paymentDetails.length > 1) {
      saveSplitPaymentCache(result.id, paymentDetails);
    }
    return result;
  } catch (error) {
    console.error('Error completing POS transaction:', error);
    throw error;
  }
}

// Get transaction history
export async function getTransactionHistory(filters?: {
  transaction_id?: string;
  from_date?: string;
  to_date?: string;
  min_amount?: number;
  max_amount?: number;
  payment_method?: POSPaymentMethod;
}): Promise<POSTransaction[]> {
  try {
    const params: any = {};
    if (filters?.from_date) params.start_date = filters.from_date;
    if (filters?.to_date) params.end_date = filters.to_date;
    if (filters?.transaction_id) params.search = filters.transaction_id;

    // Map UI payment method enums to backend values (CASH/CARD/BANK_TRANSFER)
    if (filters?.payment_method) {
      const raw = String(filters.payment_method);
      if (raw === 'CASH') params.payment_method = 'CASH';
      else if (raw === 'CARD_PAYMENT' || raw === 'MANUAL_CARD' || raw === 'CARD') params.payment_method = 'CARD';
      else if (raw === 'MOBILE_TRANSFER' || raw === 'BANK_TRANSFER') params.payment_method = 'BANK_TRANSFER';
    }
    
    const response = await apiService.get<any[]>('/pos/transactions', { params });
    
    // Format response to match frontend interface
    let transactions = response.map(mapPOSTransactionFromApi);
    
    // Apply client-side filters if needed
    if (filters?.transaction_id) {
      transactions = transactions.filter(t => 
        t.transaction_number.includes(filters.transaction_id!) ||
        t.serial.includes(filters.transaction_id!)
      );
    }
    
    if (filters?.min_amount) {
      transactions = transactions.filter(t => t.total_amount >= filters.min_amount!);
    }
    
    if (filters?.max_amount) {
      transactions = transactions.filter(t => t.total_amount <= filters.max_amount!);
    }
    
    return transactions;
  } catch (error) {
    console.error('Error fetching transaction history:', error);
    return [];
  }
}

// Get transaction by ID
export async function getTransactionById(id: number): Promise<POSTransaction | null> {
  try {
    // Enrich-only call from transaction list; keep list row if this fails.
    const response = await apiService.get<any>(`/pos/transactions/${id}`, { silentError: true });
    return mapPOSTransactionFromApi(response);
  } catch (error) {
    console.error('Error fetching transaction:', error);
    return null;
  }
}

// Refund history for a sale (previous refunds on this transaction)
export interface RefundHistoryItem {
  id: number;
  transaction_number: string;
  created_at: string;
  total_amount: number;
  item_count: number;
  items_summary: Array<{ product_id?: number; product_name: string; quantity: number; total_price: number }>;
}

export async function getRefundHistory(transactionId: number): Promise<RefundHistoryItem[]> {
  try {
    const response = await apiService.get<RefundHistoryItem[]>(`/pos/transactions/${transactionId}/refunds`);
    return Array.isArray(response) ? response.map((r: any) => ({
      id: r.id,
      transaction_number: r.transaction_number,
      created_at: r.created_at,
      total_amount: parseFloat(r.total_amount?.toString() || '0'),
      item_count: r.item_count || 0,
      items_summary: r.items_summary || [],
    })) : [];
  } catch (error) {
    console.error('Error fetching refund history:', error);
    return [];
  }
}

// Refund transaction (partial or full)
export async function refundTransaction(
  transactionId: number,
  items: Array<{ product_id: number; quantity: number }>
): Promise<POSTransaction> {
  try {
    // Get original transaction to get full item details
    const history = await getTransactionHistory();
    const original = history.find(t => t.id === transactionId);
    
    if (!original) {
      throw new Error('Transaction not found');
    }
    
    // Map refund items to POSCartItem format
    const refundItems: POSCartItem[] = original.items
      .filter(item => items.some(ref => ref.product_id === item.product_id && ref.quantity > 0))
      .map(item => {
        const refundQty = items.find(ref => ref.product_id === item.product_id)?.quantity || 0;
        return {
          id: item.id,
          product_id: item.product_id,
          product_code: item.product_code || '',
          product_name: item.product_name,
          barcode: item.barcode,
          quantity: refundQty,
          unit_price: item.unit_price,
          sale_unit_price: item.sale_unit_price ?? item.unit_price,
          total_price: item.unit_price * refundQty
        };
      });
    
    const response = await apiService.post<any>('/pos/refund', {
      transaction_id: transactionId,
      items: refundItems.map(item => ({
        product_id: item.product_id,
        product_code: item.product_code,
        product_name: item.product_name,
        barcode: item.barcode,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price
      })),
      reason: null
    });
    
    // Format response to match frontend interface
    return {
      id: response.id,
      transaction_number: response.transaction_number,
      serial: response.serial || '',
      location_id: response.location_id,
      location_name: response.location_name,
      sale_date: response.sale_date,
      sale_time: new Date(response.created_at).toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' }),
      payment_method: response.payment_method as POSPaymentMethod,
      payment_reference: response.payment_reference || undefined,
      receipt_printed: response.receipt_printed !== false,
      total_amount: parseFloat(response.total_amount.toString()),
      tax_amount: parseFloat(response.tax_amount.toString()),
      discount_amount: parseFloat(response.discount_amount.toString()),
      coupon_discount: parseFloat(response.coupon_discount?.toString() || '0'),
      served_by: response.served_by,
      cashier_name: response.served_by_name,
      created_at: response.created_at,
      items: (response.transaction_items || []).map((item: any) => {
        const up = parseFloat(item.unit_price.toString());
        const tp = parseFloat(item.total_price.toString());
        return {
          id: item.id?.toString() || '',
          product_id: item.product_id,
          product_code: item.product_code,
          product_name: item.product_name,
          barcode: item.barcode,
          quantity: item.quantity,
          unit_price: up,
          sale_unit_price: up,
          total_price: tp,
        };
      })
    };
  } catch (error) {
    console.error('Error refunding transaction:', error);
    throw error;
  }
}

// Revert (undo) a refund: restore the sale to COMPLETED and reverse stock
export async function revertRefundTransaction(transactionId: number): Promise<{ message: string; transaction_id: number }> {
  const response = await apiService.post<{ message: string; transaction_id: number }>(
    `/pos/transactions/${transactionId}/revert-refund`
  );
  return response;
}

/** Optional direct thermal print via local Windows backend (ESC/POS). */
export async function printReceiptToPrinter(
  transaction: POSTransaction,
  storeName: string,
): Promise<string> {
  const paymentLines = getReceiptPaymentLines(transaction);
  const items = transaction.items || [];

  const response = await apiService.post<{ message: string }>('/pos/print-receipt', {
    store_name: storeName,
    transaction_number: transaction.transaction_number,
    serial: transaction.serial || undefined,
    sale_date: transaction.sale_date,
    sale_time: transaction.sale_time,
    cashier_name: transaction.cashier_name,
    payment_method: transaction.payment_method,
    items: items.map((item) => ({
      product_name: item.product_name,
      product_code: item.product_code,
      barcode: item.barcode,
      quantity: item.quantity,
      unit_price: item.unit_price,
      sale_unit_price: item.sale_unit_price ?? item.unit_price,
      total_price: item.total_price,
      gst_category: item.gst_category,
      gst_amount: item.gst_amount,
    })),
    total_amount: transaction.total_amount,
    tax_amount: transaction.tax_amount ?? 0,
    discount_amount: transaction.discount_amount ?? 0,
    coupon_discount: transaction.coupon_discount ?? 0,
    payment_details:
      paymentLines.length > 0
        ? paymentLines.map((line) => ({
            payment_method: line.payment_method,
            amount: line.amount,
            reference_number: line.reference_number || undefined,
          }))
        : undefined,
    is_tax_invoice: Boolean(transaction.is_tax_invoice),
    tax_invoice_number: transaction.tax_invoice_number || undefined,
    taxable_amount: transaction.taxable_amount ?? undefined,
    exempt_amount: transaction.exempt_amount ?? undefined,
    zero_rated_amount: transaction.zero_rated_amount ?? undefined,
    supplier_tpn: transaction.supplier_tpn || undefined,
    supplier_address: transaction.supplier_address || undefined,
    buyer_name: transaction.buyer_name || undefined,
    buyer_cid: transaction.buyer_cid || undefined,
    buyer_tpn: transaction.buyer_tpn || undefined,
    buyer_address: transaction.buyer_address || undefined,
  });
  return response.message;
}

export type CreditAccountRow = {
  id: number;
  name: string;
  phone: string;
  balance: number;
};

export async function listCreditAccounts(): Promise<CreditAccountRow[]> {
  const rows = await apiService.get<CreditAccountRow[]>('/pos/credit-accounts');
  return Array.isArray(rows) ? rows : [];
}

export async function receiveCreditPayment(payload: {
  customer_id: number;
  amount: number;
  payment_method: 'CASH' | 'BANK_TRANSFER';
  reference_number?: string;
}): Promise<{ balance: number }> {
  return apiService.post('/pos/credit-payments', payload);
}

// Get coupons
export async function getCoupons(): Promise<POSCoupon[]> {
  // TODO: Replace with actual API call
  // const response = await apiService.get('/pos/coupons');
  // return response;
  return []; // Mock - no coupons by default
}

// Apply coupon
export async function validateCoupon(code: string, totalAmount: number): Promise<POSCoupon | null> {
  // TODO: Replace with actual API call
  // const response = await apiService.post('/pos/coupons/validate', { code, total_amount: totalAmount });
  // return response;
  return null; // Mock - no coupons by default
}

export { saveSplitPaymentCache, mergeSplitPaymentCache } from '../utils/posSplitPaymentCache';
